import { useEffect, type RefObject } from 'react';

/**
 * The CMS Admin app-wide form-control layer — a re-platform of
 * `view/cms.main.js` (386 LOC), which binds ~18 delegated handlers on
 * `#ksdm-content` and is the behavioural substrate **every one of the 31
 * modules writes its inputs against**.
 *
 * This is the single highest-leverage piece of the shell: without it, each
 * tranche would independently rediscover the number-spinner semantics, the
 * required-field default restore, the multi-select box, and the decimal
 * formatter — and would get them subtly different.
 *
 * Legacy binds these by delegation on a container, so a React port that used
 * per-input props would not be equivalent: the handlers must apply to markup
 * the shell has never seen. This hook keeps the delegation model and attaches
 * to the shell root, which is exactly what legacy does.
 *
 * Behaviours ported (with their `cms.main.js` line ranges):
 *  - `.number-spinner` inc/dec with `step`/`min`/`max` clamping and snapping
 *    (`changeNumberValue` :168-196), plus focus/blur class handling (:198-208).
 *  - `.text-clear` clears the previous input and fires keyup+change (:210-215).
 *  - `keyup` on `input[type=number]` — live validation, integer vs decimal by
 *    `step` (0.01/0.001/0.0001) or `data-formatter_type`, max clamp, decimal
 *    truncation, unary-minus handling (:217-283).
 *  - `blur` on `input[type=number]` — min clamp, step snapping, decimal zero
 *    padding (:290-329).
 *  - `.required-input` restores `data-default` when emptied. ★ Legacy binds
 *    this BOTH as a delegated `change` AND as a native CAPTURING listener
 *    (:59-63) so it always runs first — both are reproduced.
 *  - `dblclick input` selects the field unless readonly (:377-382).
 *  - `keypress` on `input[data-type=number]` / `[data-formatter_type]` — allows
 *    digits, one leading `-`, one `.`; blocks everything else (:331-368).
 *  - `blur` on `input[data-type=number]` — comma-separated thousands via
 *    `fn.uni_tools.formatNumberWithCommaSeparator` (:370-375).
 *  - `.press_english` keypress + paste guard (:384-400).
 *  - `.phone-number-input` keypress mask (:402-404).
 *  - The multiple-select box: toggle, item change tracking, search filter, and
 *    close-on-outside-click with a `<name>:save` broadcast (:74-166).
 *  - Esc / Enter on an active `.popup-wrapper` (:45-57).
 *  - `#left_resizer` change shifts the footer (`move_footer`, :65-72).
 *
 * `fn.uni_tools` lives in the shared `universals/universal_app.js` global that
 * `react.html` does not load, so the two helpers it provides
 * (`formatNumberWithCommaSeparator`, `accesptOnlyEnglish`, `mobileKeyPress`)
 * are implemented locally here, matching the legacy behaviour.
 */

/**
 * `fn.uni_tools.formatNumberWithCommaSeparator` — VERBATIM port.
 *
 * ★★ CORRECTED IN T6 (2026-08-09). The previous version re-derived the string
 * from `Number(value)`, which DROPS a `toFixed(2)` trailing zero whenever no
 * comma grouping applies — measured on the Dynamic Sizer's width box: legacy
 * displays "5.50" after blur, the old shell path wrote "5.5". The legacy
 * function (`universal_fn_tools.js:976-980`) only returns the Number-derived
 * string when it actually inserted a comma; otherwise it returns the ORIGINAL
 * argument stringified:
 *     if (isNaN(Number(number))) return number;
 *     const n = Number(number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
 *     return n.includes(',') ? n : String(number);
 * Every quirk of that (including "1,234.5" LOSING its pad once commas appear,
 * and non-numeric input echoing back verbatim) is now reproduced.
 */
export const formatWithCommas = (value: number | string | undefined): string => {
  if (Number.isNaN(Number(value))) return String(value);
  const newNumber = Number(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return newNumber.includes(',') ? newNumber : String(value);
};

/** Legacy's English-only guard (`fn.uni_tools.accesptOnlyEnglish`). */
const ENGLISH_ONLY = /^[A-Za-z0-9\s.,!?@#$%^&*()_\-+=:;"'<>/\\[\]{}|`~]*$/;

const isDecimalFormat = (input: HTMLInputElement): boolean => {
  const step = Number(input.step || input.dataset.step || 1);
  return (
    step === 0.01 ||
    step === 0.001 ||
    step === 0.0001 ||
    input.getAttribute('data-formatter_type') === 'decimal'
  );
};

const restoreRequiredDefault = (target: EventTarget | null) => {
  const el = target as HTMLInputElement | null;
  if (!el || !el.classList?.contains('required-input')) return;
  if (el.value.length === 0 && el.dataset.default !== undefined) {
    el.value = el.dataset.default;
  }
};

export interface CmsFormControlsOptions {
  /** Broadcast when a multi-select closes — legacy fires `<name>:save`. */
  onMultiSelectSave?: (name: string) => void;
}

export function useCmsFormControls(
  rootRef: RefObject<HTMLElement>,
  options: CmsFormControlsOptions = {}
): void {
  const { onMultiSelectSave } = options;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const q = <T extends Element>(sel: string) => root.querySelectorAll<T>(sel);

    // ── required-input default restore, CAPTURING (cms.main.js:59-63) ───────
    // Legacy attaches this with `true` so it always runs before any delegated
    // handler. Reproduced exactly; the delegated `change` below is the second
    // binding legacy also has.
    const onCapturingChange = (e: Event) => restoreRequiredDefault(e.target);
    root.addEventListener('change', onCapturingChange, true);

    // ── multiple-select open/close (cms.main.js:67-75, 110-120) ────────────
    /** `hide_multiple_select` (:114-120) — set-wide, and clears `.changed`. */
    const hideMultiSelect = (name: string) => {
      root.querySelectorAll<HTMLElement>(`.multiple-select-list[data-for="${name}"]`).forEach((l) => {
        l.classList.remove('active');
        l.querySelectorAll<HTMLInputElement>('input.changed').forEach((i) => i.classList.remove('changed'));
      });
    };

    /**
     * `close_opened_boxes` (:67-75) — VERBATIM port of the condition:
     *
     *   var name = $('.multiple-select-list.active').data('for');
     *   if (!elem.parents('.multiple-select-list.active[data-for="'+name+'"]').html() &&
     *       name && (elem.prop('name') != name || !elem.hasClass('multiple-select-ctrl-btn'))) {
     *     Dispatcher.trigger(name + ':save');
     *     self.hide_multiple_select(name);
     *   }
     *
     * ★ `parents()`, NOT `closest()` — jQuery's `parents` EXCLUDES the element
     * itself, so a click landing on the list div's own padding (rather than on
     * one of its `<label>`s) closes the box on legacy. That is not a corner
     * case: measured on the fan Settings dropdown, 527 of 1023 sampled points
     * inside the box hit the div itself, i.e. roughly half its area. Hence
     * `t.parentElement?.closest(...)`.
     *
     * ★ The third clause is the guard that lets a click on the box's OWN
     * control button through untouched, so `toggleMultiSelect` below can do the
     * open↔close toggle without this handler having already closed it.
     */
    const closeOpenedBoxes = (t: HTMLElement) => {
      const name = root.querySelector<HTMLElement>('.multiple-select-list.active')?.dataset.for;
      if (!name) return;
      if (t.parentElement?.closest(`.multiple-select-list.active[data-for="${name}"]`)) return;
      const isOwnCtrlBtn =
        (t as HTMLButtonElement).name === name && t.classList.contains('multiple-select-ctrl-btn');
      if (isOwnCtrlBtn) return;
      onMultiSelectSave?.(name);
      hideMultiSelect(name);
    };

    // ── click delegation ───────────────────────────────────────────────────
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      // .number-spinner (cms.main.js:168-196)
      if (t.classList.contains('number-spinner')) {
        const dec = t.classList.contains('number-spinner-desc');
        const el = (dec ? t.nextElementSibling : t.previousElementSibling) as HTMLInputElement | null;
        if (el) {
          const rawStep = el.step ? Number(el.step) : 1;
          const step = dec ? -rawStep : rawStep;
          el.classList.add('focus');
          const min = el.min ? Number(el.min) : -Infinity;
          const max = el.max ? Number(el.max) : Infinity;
          let value = el.value ? Number(el.value) : min;
          el.value = String(value);
          if (value + step >= min && value + step <= max) {
            value = Math.round((value + step) / step) * step;
            el.value = String(value);
            el.dispatchEvent(new Event('keyup', { bubbles: true }));
          } else if (value + step < min) {
            el.value = String(min);
          } else if (value + step > max) {
            el.value = String(max);
          }
        }
      }

      // .text-clear (cms.main.js:210-215)
      if (t.classList.contains('text-clear')) {
        const input = t.previousElementSibling as HTMLInputElement | null;
        if (input) {
          input.value = '';
          input.dispatchEvent(new Event('keyup', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // multiple-select toggle (cms.main.js:96-120 `toggle_multiple_select` +
      // `show/hide_multiple_select`).
      // ★★ SET-WIDE, NOT SINGLE-ELEMENT — T7 (cabinet) fix, the SAME class of bug
      // T5 fixed for `.popup-wrapper` below. Legacy operates on
      // `self.$el.find('.multiple-select-list[data-for="' + name + '"]')`, a
      // jQuery SET: `show_multiple_select` `.addClass('active')` and
      // `hide_multiple_select` `.removeClass('active')` apply to EVERY list with
      // that `data-for`, and `hasClass` is true if ANY is active. This file used
      // `querySelector` (FIRST match only). It is invisible until a screen mounts
      // two multi-selects with the SAME `data-for`: cabinet's `internal_wall` AND
      // `d_mount` both author `data-for="plane_list"`, and cnstrSettings' int_wall
      // adds one per construction class. Measured on `:8130`: clicking d_mount's
      // `plane_list` button `.addClass('active')`s BOTH plane lists (only the
      // visible tab's shows); the first-match port added `active` to
      // internal_wall's (DOM-earlier, hidden) list, so d_mount's dropdown never
      // opened. Fixed at the shell, matching legacy + the T5 precedent.
      if (t.classList.contains('multiple-select-ctrl-btn')) {
        const name = (t as HTMLButtonElement).name;
        /**
         * ★★ CLOSE ANY OTHER OPEN BOX FIRST — reported 2026-09-10 (fan
         * Settings), and the SAME omission behind both halves of that report.
         *
         * `toggle_multiple_select` (:96-109) opens with:
         *     if (self.$el.find('.multiple-select-list').hasClass('active'))
         *       self.close_opened_boxes(e);
         * i.e. before toggling, legacy runs the outside-click handler by hand,
         * which closes whichever OTHER box is open (the `elem.prop('name') !=
         * name` clause spares this button's own). This port skipped that line
         * and `return`ed straight past `close_opened_boxes` below, so opened
         * boxes ACCUMULATED — and that is why clicking outside then "did not
         * work" either: `close_opened_boxes` only ever closes ONE `data-for`
         * per click (legacy's too — it just never has more than one open), so
         * with N boxes open it took N clicks, closing them DOM-order rather
         * than the one the user was looking at.
         *
         * Measured on `/admin/#products/1/components/fans` → Settings, before:
         *     open #0, open #1        → both active
         *     click outside           → #1 still active
         *     click outside again     → closed
         * legacy, and this file now: opening #1 closes #0, one outside click
         * closes #1.
         */
        closeOpenedBoxes(t);
        const lists = root.querySelectorAll<HTMLElement>(
          `.multiple-select-list[data-for="${name}"]`
        );
        if (lists.length) {
          const anyActive = Array.from(lists).some((l) => l.classList.contains('active'));
          if (anyActive) {
            onMultiSelectSave?.(name);
            hideMultiSelect(name);
          } else {
            lists.forEach((l) => l.classList.add('active'));
          }
        }
        return;
      }

      // close-on-outside-click (cms.main.js:67-75 `close_opened_boxes`, bound
      // as the bare `'click'` entry in the events hash).
      closeOpenedBoxes(t);
    };
    root.addEventListener('click', onClick);

    // ── keyup: live number validation (cms.main.js:217-283) ────────────────
    const onKeyUp = (e: KeyboardEvent) => {
      const input = e.target as HTMLInputElement | null;
      if (!input || input.type !== 'number') {
        // multiple-select search (cms.main.js:145-166)
        const el = e.target as HTMLInputElement | null;
        if (el?.classList.contains('multiple-select-search')) applySearch(el);
        return;
      }
      validateNumber(input, e.keyCode);
    };

    const applySearch = (el: HTMLInputElement) => {
      const value = (el.name || '').replace('_search', '');
      // set-wide, matching `search_multiple_select_items` (cms.main.js:122-129).
      const lists = root.querySelectorAll<HTMLElement>(`.multiple-select-list[data-for="${value}"]`);
      if (!lists.length) return;
      const term = el.value.toLowerCase();
      lists.forEach((list) => {
        list.classList.toggle('searching', term.length > 0);
        list.querySelectorAll<HTMLElement>('li').forEach((li) => {
          li.classList.remove('search-resault');
          const label = li.querySelector('.label')?.textContent?.toLowerCase() ?? '';
          if (label.indexOf(term) !== -1) li.classList.add('search-resault');
        });
      });
    };

    const validateNumber = (input: HTMLInputElement, key: number) => {
      input.type = 'number';
      let value = Number(input.value);
      const min = input.min ? Number(input.min) : -Infinity;
      const max = input.max ? Number(input.max) : Infinity;
      const step = input.step ? Number(input.step) : 1;
      const format = isDecimalFormat(input) ? 'decimal' : 'integer';
      const index = input.value.indexOf('.');

      if (value > max) {
        value = max;
        input.value = String(value);
      }
      if (format === 'integer') {
        if (key === 110 || index !== -1) {
          input.value = '';
          value = Math.round(value);
          if (value > max) value = max;
          input.value = String(value);
        }
      } else {
        const multiplier = step === 0.01 ? 100 : step === 0.0001 ? 10000 : 1000;
        if (index !== -1 && input.value.length > index + 2) {
          const rounded = Math.round(value * multiplier) / multiplier;
          /**
           * ★ DELIBERATE DEVIATION FROM LEGACY (reported 2026-09-10) — only
           * rewrite the box when rounding actually CHANGES the number.
           *
           * Legacy re-serialises unconditionally, and `String(Number(...))`
           * drops the zeros the user is still typing through: the rewrite puts
           * the caret at the end of a value one character shorter than what
           * they typed. At step 0.01 that is invisible, because every 2-decimal
           * value survives the round trip ("0.25" → 0.25 → "0.25"). At step
           * 0.0001 it makes 4-decimal entry impossible — typing `0.0016` passes
           * through the state "0.00", which becomes "0", and the remaining
           * keystrokes land on it as "016".
           *
           * Skipping a no-op write cannot change any value legacy produced: the
           * step is still enforced the moment a digit beyond it is typed
           * ("0.00161" → "0.0016"), and blur (`onFocusOut`) still re-serialises
           * and pads unconditionally. All it leaves alone is the literal text
           * mid-edit, which blur normalises anyway.
           */
          if (rounded !== value) input.value = String(rounded);
          value = rounded;
        }
        if (key === 110) {
          if (value < min || !input.value.length) {
            value = min;
            input.value = String(value);
          } else if (index !== -1) {
            input.value = '';
            input.value = String(value);
          }
        }
      }
      // unary minus: keyCode 109 (numpad) or 189 (main row)
      if (min >= 0 && (key === 109 || key === 189)) {
        if (input.value.length) {
          value *= -1;
          input.value = String(value);
        } else {
          input.value = '';
        }
      }
      if (
        input.classList.contains('required-input') &&
        document.activeElement !== input &&
        input.value.length === 0 &&
        input.dataset.default !== undefined
      ) {
        input.value = input.dataset.default;
      }
    };
    root.addEventListener('keyup', onKeyUp);

    // ── keypress guards (cms.main.js:331-368, 384-404) ─────────────────────
    const onKeyPress = (e: KeyboardEvent) => {
      const input = e.target as HTMLInputElement | null;
      if (!input) return;

      if (input.classList.contains('press_english')) {
        if (!ENGLISH_ONLY.test(e.key)) e.preventDefault();
        return;
      }
      if (input.classList.contains('phone-number-input')) {
        if (!/[0-9()\-+ ]/.test(e.key) && e.key.length === 1) e.preventDefault();
        return;
      }
      const isNumeric =
        input.dataset.type === 'number' ||
        input.getAttribute('data-formatter_type') === 'decimal' ||
        input.getAttribute('data-formatter_type') === 'integer';
      if (!isNumeric) return;

      const key = e.key;
      const value = input.value;
      const cursorPos = input.selectionStart ?? 0;
      if ([8, 37, 38, 39, 40].includes(e.keyCode)) return;
      if (e.keyCode >= 48 && e.keyCode <= 57) return;
      if (key === '-') {
        if (cursorPos !== 0 || value.includes('-')) e.preventDefault();
        return;
      }
      if (key === '.') {
        if (value.includes('.')) e.preventDefault();
        return;
      }
      e.preventDefault();
    };
    root.addEventListener('keypress', onKeyPress);

    // ── paste guard for .press_english (cms.main.js:386-398) ───────────────
    const onPaste = (e: ClipboardEvent) => {
      const input = e.target as HTMLInputElement | null;
      if (!input?.classList.contains('press_english')) return;
      const text = e.clipboardData?.getData('text') ?? '';
      if (!ENGLISH_ONLY.test(text)) e.preventDefault();
    };
    root.addEventListener('paste', onPaste);

    // ── blur / focusout (cms.main.js:198-208, 290-329, 370-375) ────────────
    const onFocusOut = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;

      if (t.classList.contains('number-spinner')) {
        const dec = t.classList.contains('number-spinner-desc');
        const el = (dec ? t.nextElementSibling : t.previousElementSibling) as HTMLInputElement | null;
        if (el) {
          el.classList.remove('focus');
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }

      const input = t as HTMLInputElement;
      if (input.tagName !== 'INPUT') return;

      if (input.type === 'number') {
        const min = input.min ? Number(input.min) : -Infinity;
        const step = input.step ? Number(input.step) : 1;
        let value: number | string = input.value ? Number(input.value) : '';
        if (value !== '' && (value as number) < min) value = min;
        if (value === '') {
          input.value = '';
          return;
        }
        if (step === 1) value = Math.round(value as number);
        else if (((value as number) - min) % step !== 0)
          value = Math.round((value as number) / step) * step;
        input.value = String(value);

        if (!Number.isInteger(step)) {
          const decimals = String(step).split('.')[1]?.length ?? 0;
          let s = input.value;
          let current = s.indexOf('.') > 0 ? s.split('.')[1].length : 0;
          if (current === 0) s += '.';
          while (decimals > current) {
            s += '0';
            current += 1;
          }
          if (s.indexOf('.') !== s.length - decimals - 1) {
            s = s.substring(0, s.indexOf('.') + decimals + 1);
          }
          input.value = s;
        }
        return;
      }

      if (input.dataset.type === 'number') {
        /**
         * `numberBlurValidation` (cms.main.js:344-355) — VERBATIM port.
         *
         * ★★ CORRECTED IN T6 (2026-08-09), two deviations found via the
         * Dynamic Sizer's width box (legacy "5.50" vs the old path's "5.5"):
         *  1. legacy passes the `toFixed(2)` STRING through
         *     `formatNumberWithCommaSeparator`, which preserves it when no
         *     comma is inserted — see `formatWithCommas` above;
         *  2. legacy's DECIMAL test here is `(data('step')||attr('step')) ==
         *     0.01 || data-formatter_type === 'decimal'` — ONLY 0.01, unlike
         *     the keyup path's 0.01/0.001/0.0001 (`isDecimalFormat`). The old
         *     shell reused `isDecimalFormat` for blur, silently treating a
         *     step-0.001 box as decimal where legacy formats it as integer.
         * Also faithful: a non-numeric leftover becomes the literal "NaN"
         * (legacy `Number(...)` → NaN → toFixed → "NaN" → echoed), and an
         * empty box stays empty.
         */
        const raw = input.value;
        if (!raw.length) return;
        const stepAttr = input.dataset.step ?? input.getAttribute('step');
        const decimal =
          Number(stepAttr) === 0.01 ||
          input.getAttribute('data-formatter_type') === 'decimal';
        const n = Number(raw.replace(/,/g, ''));
        const formatted = decimal ? n.toFixed(2) : n;
        input.value = formatWithCommas(formatted);
      }
    };
    /**
     * ★ BUBBLE PHASE, NOT CAPTURE — corrected in T1 (2026-08-05).
     *
     * Run 1 bound this with `capture: true`, mirroring the `change` listener
     * below. That was wrong. `cms.main.js` binds `change` with capture
     * EXPLICITLY and deliberately (`:43-47`, with the comment
     * `true // capturing phase = always runs first`), but every one of its blur
     * handlers — `'blur .number-spinner'` (:16), `'blur input[type="number"]'`
     * (:20) and `'blur input[data-type="number"]'` (:32) — lives in the Backbone
     * `events` hash, i.e. jQuery DELEGATION, which fires in the BUBBLE phase.
     * (jQuery routes delegated `blur` through `focusout` precisely because
     * `blur` itself does not bubble.) So a screen's own blur handler runs FIRST
     * on legacy, and the shell's formatter second.
     *
     * The difference is observable. MEASURED on `numbering` — type `007` into a
     * `seq_non_ending_num` box and blur:
     *     legacy   PATCH {"non_ending_num":"007"}, then the box normalises to `7`
     *     capture  PATCH {"non_ending_num":"7"}    (shell normalised first)
     * Same stored value in that module, but ANY CMS screen that PATCHes a STRING
     * field on blur would put different bytes on the wire. Found by T1's fifth
     * module; fixed in the shell rather than worked around in `numbering`, which
     * is the whole reason the quiet leaves go first.
     */
    root.addEventListener('focusout', onFocusOut);

    // ── delegated change: required default + multi-select item marking ─────
    const onChange = (e: Event) => {
      const t = e.target as HTMLInputElement | null;
      if (!t) return;
      restoreRequiredDefault(t);
      if (t.classList.contains('multiple_select_item')) t.classList.toggle('changed');
      if (t.id === 'left_resizer') {
        // move_footer (cms.main.js:100-108)
        const footerDiv = document.querySelector('footer > div');
        if (footerDiv) {
          if (t.checked) {
            footerDiv.classList.add('to-left');
            footerDiv.classList.remove('col-xs-10', 'col-xs-offset-2');
          } else {
            footerDiv.classList.remove('to-left');
            footerDiv.classList.add('col-xs-10', 'col-xs-offset-2');
          }
        }
      }
    };
    root.addEventListener('change', onChange);

    // ── dblclick selects the field unless readonly (cms.main.js:377-382) ────
    const onDblClick = (e: MouseEvent) => {
      const el = e.target as HTMLInputElement | null;
      if (el?.tagName === 'INPUT' && !el.readOnly) el.select();
    };
    root.addEventListener('dblclick', onDblClick);

    // ── search event (clear button in a type=search box) ────────────────────
    const onSearch = (e: Event) => {
      const el = e.target as HTMLInputElement | null;
      if (el?.classList.contains('multiple-select-search')) applySearch(el);
    };
    root.addEventListener('search', onSearch);

    // ── Esc / Enter on an active popup (cms.main.js:45-57) ──────────────────
    // Legacy binds this on <body>, not on the container, so it fires even when
    // the popup is portalled outside #ksdm-content.
    /**
     * ★ SET-WIDE, NOT SINGLE-ELEMENT — corrected in T5 (2026-08-09).
     *
     * All three of these were `querySelector` (first match only). Legacy uses
     * jQuery, where every one of them operates on the WHOLE matched set:
     *   `$('.popup-wrapper.active button[name="cancel"]').click()`   ← clicks ALL
     *   `$('.popup-wrapper.active').remove()`                        ← removes ALL
     *   `$('…button[autofocus="true"]').trigger('click')`            ← clicks ALL
     * (`cms.main.js:52-58`). With two active popups, legacy closes both and the
     * single-element version closed one — leaving a live overlay behind.
     *
     * ⚠ The branch is on the whole SET, not per popup: if ANY active popup has a
     * cancel button, legacy clicks every cancel button and removes nothing;
     * only when there is none does it remove every active popup.
     *
     * Found by T5's verifier, which also proved it is **not reachable from the
     * permissions screens** (the active overlay is full-screen at `z-index:1000`
     * and intercepts the pointer, so a second popup cannot be opened by a real
     * gesture). Fixed in the shell anyway rather than parked: it is shared by all
     * 31 modules, and a later tranche that CAN stack two popups would otherwise
     * inherit a silent divergence.
     */
    const onBodyKeyUp = (e: KeyboardEvent) => {
      const active = document.querySelectorAll<HTMLElement>('.popup-wrapper.active');
      if (active.length < 1) return;
      if (e.which === 27) {
        const cancels = document.querySelectorAll<HTMLButtonElement>(
          '.popup-wrapper.active button[name="cancel"]'
        );
        if (cancels.length > 0) cancels.forEach((btn) => btn.click());
        else active.forEach((popup) => popup.remove());
      }
      if (e.which === 13) {
        document
          .querySelectorAll<HTMLButtonElement>(
            '.popup-wrapper.active button[autofocus="true"]'
          )
          .forEach((btn) => btn.click());
      }
    };
    document.body.addEventListener('keyup', onBodyKeyUp);

    void q; // (kept for tranches that need direct queries)

    return () => {
      root.removeEventListener('change', onCapturingChange, true);
      root.removeEventListener('click', onClick);
      root.removeEventListener('keyup', onKeyUp);
      root.removeEventListener('keypress', onKeyPress);
      root.removeEventListener('paste', onPaste);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('change', onChange);
      root.removeEventListener('dblclick', onDblClick);
      root.removeEventListener('search', onSearch);
      document.body.removeEventListener('keyup', onBodyKeyUp);
    };
  }, [rootRef, onMultiSelectSave]);
}
